import { lazy, Suspense, useEffect, useRef, useState, useSyncExternalStore, type ReactNode } from 'react';
import * as D from '@radix-ui/react-dialog';
import { t } from '../../i18n/index.js';

// Inhalt erst beim Öffnen laden (Startpaket-Budget a3)
const PaletteBody = lazy(() => import('./PaletteBody.js').then((m) => ({ default: m.PaletteBody })));
const ShortcutList = lazy(() => import('./PaletteBody.js').then((m) => ({ default: m.ShortcutList })));

/**
 * Befehlspalette (Paket P7, Strg/⌘ + K) – einer der drei projektübergreifenden Orte: springen, suchen, handeln.
 * Seiten melden ihre Aktionen über `usePaletteActions` an (z. B. „Neue Aufgabe“ im Projekt); Aufgaben und Projekte
 * findet die Serversuche – nur, was die Person sehen darf.
 */

export interface PaletteAction {
  id: string;
  label: string;
  /** zusätzliche Suchwörter */
  keywords?: string;
  hint?: string;
  icon?: ReactNode;
  run: () => void;
}

/* ---------- Anmeldung von Aktionen durch Seiten ---------- */

const registry = new Map<string, PaletteAction[]>();
let snapshot: PaletteAction[] = [];
const listeners = new Set<() => void>();
const publish = () => {
  snapshot = [...registry.values()].flat();
  listeners.forEach((l) => l());
};

export function usePaletteActions(key: string, actions: PaletteAction[]) {
  const ref = useRef(actions);
  ref.current = actions;
  const sig = actions.map((a) => `${a.id}:${a.label}`).join('|');
  useEffect(() => {
    // Ausführen über die Referenz: immer der aktuelle Stand der Seite
    registry.set(key, ref.current.map((a, i) => ({ ...a, run: () => ref.current[i]?.run() })));
    publish();
    return () => {
      registry.delete(key);
      publish();
    };
  }, [key, sig]);
}

export const useContextActions = () =>
  useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => {
        listeners.delete(fn);
      };
    },
    () => snapshot,
  );

/* ---------- Öffnen von überall ---------- */

const OPEN = 'werft:palette';
const SHORTCUTS = 'werft:shortcuts';
export const openPalette = (query = '') => window.dispatchEvent(new CustomEvent(OPEN, { detail: query }));
export const openShortcuts = () => window.dispatchEvent(new Event(SHORTCUTS));
export const openInbox = () => window.dispatchEvent(new Event('werft:inbox'));

const typing = (el: EventTarget | null) => el instanceof HTMLElement && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName));

/** Einmal je Seite einbinden (Shell): Tastenkürzel Strg/⌘ + K und „?“ */
export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [initialQuery, setInitialQuery] = useState('');
  const [help, setHelp] = useState(false);
  useEffect(() => {
    const onKey = (e: globalThis.KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setInitialQuery('');
        setOpen((o) => !o);
      } else if (e.key === '?' && !typing(e.target) && !e.ctrlKey && !e.metaKey) {
        e.preventDefault();
        setHelp(true);
      }
    };
    const onOpen = (e: Event) => {
      setInitialQuery(typeof (e as CustomEvent).detail === 'string' ? (e as CustomEvent).detail : '');
      setOpen(true);
    };
    const onHelp = () => setHelp(true);
    window.addEventListener('keydown', onKey);
    window.addEventListener(OPEN, onOpen);
    window.addEventListener(SHORTCUTS, onHelp);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener(OPEN, onOpen);
      window.removeEventListener(SHORTCUTS, onHelp);
    };
  }, []);
  return (
    <>
      <D.Root open={open} onOpenChange={setOpen}>
        <D.Portal>
          <D.Overlay className="fixed inset-0 z-50 bg-black/30 dark:bg-black/60" />
          <D.Content
            aria-describedby={undefined}
            className="fixed top-[10vh] left-1/2 z-50 w-[calc(100vw-24px)] max-w-xl -translate-x-1/2 overflow-hidden rounded-2xl bg-white shadow-2xl outline-none dark:bg-zinc-900"
          >
            <D.Title className="sr-only">{t('palette.title')}</D.Title>
            {open && (
              <Suspense fallback={<p className="p-4 text-sm text-zinc-500">{t('common.loading')}</p>}>
                <PaletteBody initialQuery={initialQuery} onClose={() => { setOpen(false); setInitialQuery(''); }} onHelp={() => setHelp(true)} />
              </Suspense>
            )}
          </D.Content>
        </D.Portal>
      </D.Root>
      <D.Root open={help} onOpenChange={setHelp}>
        <D.Portal>
          <D.Overlay className="fixed inset-0 z-50 bg-black/30 dark:bg-black/60" />
          <D.Content
            aria-describedby={undefined}
            className="fixed top-1/2 left-1/2 z-50 max-h-[85vh] w-[calc(100vw-24px)] max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-2xl bg-white p-4 shadow-2xl outline-none dark:bg-zinc-900"
          >
            <D.Title className="text-lg font-semibold">{t('keys.title')}</D.Title>
            {help && (
              <Suspense fallback={null}>
                <ShortcutList />
              </Suspense>
            )}
          </D.Content>
        </D.Portal>
      </D.Root>
    </>
  );
}

